import { iconGet, Variant } from "@process-creative/slate-theme-tools";
import { SlateCustomEvent } from "@process-creative/slate-theme-tools/dist/support";

// Set up the inner template
const tmpl = document.createElement('template');
tmpl.innerHTML = `
  <slot></slot>
`;

export const EVT_QUANTITY_CHANGED = 'onQuantityChanged';

export class QuantityChangedEvent extends SlateCustomEvent<{
  old: number; quantity: number; qtySelector: QuantitySelectorElement;
}> {
  constructor(old: number, quantity: number, qs: QuantitySelectorElement) {
    super(EVT_QUANTITY_CHANGED, {
      bubbles: true,
      cancelable: false,
      detail: {
        old, quantity, qtySelector: qs
      }
    })
  }
}

type QuantitySelectorGenerateParams = {
  min?: number | null;
  max?: number | null;
  name?: string;
  quantity?: number;
  class?: string;
  attributes?: string;
  minusClass?: string;
  plusClass?: string;
}
export class QuantitySelectorElement extends HTMLElement {
  public static getMinMaxForVariant(variant: Variant, min: number = 0) {
    return (
      variant.inventory_management === 'shopify' &&
      variant.inventory_policy === 'deny'
    ) ? { max: variant.inventory_quantity, min } : { min };
  }

  public static generate(p: QuantitySelectorGenerateParams) {
    return `
      <quantity-selector class="${p.class || ''}" ${p.attributes || ''}>
        ${QuantitySelectorElement.generateContents(p)}
      </quantity-selector>
    `;
  }

  private static generateContents(p: QuantitySelectorGenerateParams) {
    return `
      <button type="button" class="o-quantity-selector__btn is-minus ${p.minusClass || ''}">
        ${iconGet('minus')}
      </button>
      <input
        type="number"
        class="o-quantity-selector__input"
        name="${p.name || 'quantity'}"
        value="${p.quantity || 0}"
        ${typeof p.min === 'undefined' || p.min === null ? '' : `min="${p.min}"`}
        ${typeof p.max === 'undefined' || p.max === null ? '' : `min="${p.max}"`}
      />
      <button type="button" class="o-quantity-selector__btn is-plus ${p.plusClass || ''}">
        ${iconGet('plus')}
      </button>
    `;
  }

  // Getters/Setters
  get value(): number {
    return this.input ? this.input.valueAsNumber : this.fallbackValue;
  }

  set value(value: number) {
    if (this.max !== null) value = Math.min(this.max, value);
    if (this.min !== null) value = Math.max(this.min, value);
    const curValue = this.value;
    if (this.input && this.input.valueAsNumber != value) {
      this.input.valueAsNumber = value;
    }
    if (value != curValue) {
      this.dispatchEvent(new QuantityChangedEvent(curValue, value, this));
    }
    if (!isNaN(value)) this.fallbackValue = value;
  }

  get min(): number | null {
    const strMin = this.input ? this.input.min : this.fallbackMin;
    if (strMin === null) return null;
    if (typeof strMin === typeof '') {
      const iMin = parseInt(strMin as string);
      if (!(strMin as string).replace(/\s/g, '').length) return null;
      if (isNaN(iMin)) return null;
    }
    return strMin as number;
  }

  set min(value: number | null) {
    if (value === null) {
      if (this.input) this.input.min = '';
      this.fallbackMin = null;
    } else {
      if (this.input) this.input.min = value.toString();
      this.fallbackMin = this.fallbackMin;
    }
    this.value = this.value;
  }

  get max(): number | null {
    const strMax = this.input ? this.input.max : this.fallbackMax;
    if (strMax === null) return null;
    if (typeof strMax === typeof '') {
      const iMax = parseInt(strMax as string);
      if (!(strMax as string).replace(/\s/g, '').length) return null;
      if (isNaN(iMax)) return null;
    }
    return strMax as number;
  }

  set max(value: number | null) {
    if (value === null) {
      if (this.input) this.input.max = '';
      this.fallbackMax = null;
    } else {
      if (this.input) this.input.max = value.toString();
      this.fallbackMax = this.fallbackMin;
    }
    this.value = this.value;
  }

  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }

  set disabled(disabled: boolean) {
    if (disabled) {
      this.setAttribute('disabled', 'disabled');
    } else {
      this.removeAttribute('disabled');
    }
    this.updateDisabled();
  }

  // Properties
  private readonly input: HTMLInputElement | null = null;
  private readonly btnMinus: HTMLButtonElement | null = null;
  private readonly btnPlus: HTMLButtonElement | null = null;

  private fallbackValue: number = 0;
  private fallbackMin: number | null = null;
  private fallbackMax: number | null = null;

  constructor() {
    super();

    // Set up the shadow
    const sr = this.attachShadow({ mode: 'open' });
    sr.appendChild(tmpl.content.cloneNode(true));

    // Get the children
    this.btnMinus = this.querySelector('.is-minus');
    this.btnPlus = this.querySelector('.is-plus');
    this.input = this.querySelector('input[type="number"]');

    // Set up class lists
    this.classList.add('o-quantity-selector');

    // Add event listeners
    this.btnMinus?.addEventListener('click', this.onMinusClick);
    this.btnPlus?.addEventListener('click', this.onPlusClick);
    this.input?.addEventListener('input', this.onChange);

    this.updateDisabled();

    this.value = this.value;
  }

  private onPlusClick = (e: MouseEvent) => this.value += 1;
  private onMinusClick = (e: MouseEvent) => this.value -= 1;

  private onChange = (e: HTMLElementEventMap['input']) => {
    if (!this.input) return;
    if (!this.input.value.length) return;
    this.value = this.input.valueAsNumber;
    this.dispatchEvent(new QuantityChangedEvent(
      this.fallbackValue, this.value, this
    ));
  }

  private updateDisabled() {
    const de = (n?: (HTMLElement & { disabled: boolean }) | null) => {
      if (!n) return;
      n.disabled = true;
      n.classList.add('is-disabled');
    }

    const ee = (n?: (HTMLElement & { disabled: boolean }) | null) => {
      if (!n) return;
      n.disabled = false;
      n.classList.remove('is-disabled');
    }

    // Disable / Enable the children
    [
      this.btnMinus, this.btnPlus, this.input
    ].forEach(e => {
      this.disabled ? de(e) : ee(e);
    })

    if (this.disabled) {
      this.classList.add('is-disabled');
    } else {
      this.classList.remove('is-disabled');
    }
  }
}

if (!customElements.get('quantity-selector')) {
  customElements.define('quantity-selector', QuantitySelectorElement);
}